import { queryClient } from '@/shared/api';
import { useAuthStore } from '@/shared/model';
import { canAccessViewFull, getViewState } from '@aegislab/shared';
import type {
  AppView,
  TeamCapability,
  TeamRole,
  UserLevel,
  UserRole,
  ViewEligibility,
} from '@aegislab/shared';

/**
 * ⚠️ 이 파일의 `queryKey` 는 **의도적으로 리터럴**이다 — 키 팩토리를 쓰지 않는다.
 *
 * 아래 모든 엔티티 접근이 `queryFn` 안의 동적 `import()` 인 것은 코드 스플리팅 때문이다.
 * 이 모듈은 독(항상 화면에 있음)과 함께 로드되므로, 팩토리를 정적 import 하면 그 엔티티 배럴이
 * 독 청크로 딸려 들어온다 — `@/entities/attendance` 배럴은 `TeamAttendanceTable` ·
 * `AuditLogSection` 같은 **UI 컴포넌트까지 재수출**한다. 워밍하려고 워밍 대상을 미리 싣는 꼴이다.
 *
 * 대가는 **드리프트 위험**이다: 팩토리에서 키 모양을 바꾸면 여기 리터럴은 따라오지 않고,
 * 워밍이 딴 키를 채워 캐시 미스가 조용히 생긴다(에러는 안 난다). 키를 바꿀 때 이 파일을
 * 함께 열 것. 근본 해결은 키 모듈을 배럴 밖에서 참조할 수 있게 되는 것인데,
 * `fsd/no-public-api-sidestep` 이 CI 차단이라 지금은 막혀 있다.
 */
const PREFETCH_STALE_TIME = 5 * 60_000;
const HOVER_DEBOUNCE_MS = 300;

/**
 * `features/bug-report` 의 `LIST_PAGE_LIMIT` 과 **같아야 한다.**
 *
 * 값을 정적 import 하지 않는 이유는 위 도크스트링과 같다(독 청크에 feature 를 끌어오지 않는다).
 * 어긋나면 첫 페이지 크기만 달라지고 커서 기반이라 동작은 하지만, 워밍한 페이지를 화면이
 * 그대로 쓰지 못해 이득이 준다.
 */
const BUG_REPORT_LIST_PAGE_LIMIT = 100;

const pendingTimers = new Map<AppView, ReturnType<typeof setTimeout>>();

export type ViewComponentPreloaders = Partial<Record<AppView, () => Promise<unknown>>>;

/**
 * 페이지 청크 preloader 는 화면 목록(pages)을 참조해야 하는데 위젯은 상위 레이어를 import 할 수
 * 없다(FSD 레이어 규칙). 그래서 정본은 `app/config/viewComponentPreloaders` 에 두고 앱 초기화가
 * 여기에 주입한다. 등록 전이면 데이터 prefetch 만 돌고 청크 워밍만 건너뛴다 — 워밍은 성능 최적화라
 * 없어도 기능은 그대로다(클릭 시 Suspense 폴백을 한 번 더 보는 정도).
 */
let componentPreloaders: ViewComponentPreloaders = {};

/** 앱 초기화(app/providers)가 한 번 호출한다. */
export function registerViewComponentPreloaders(preloaders: ViewComponentPreloaders): void {
  componentPreloaders = preloaders;
}

type PrefetchEntry = {
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
  /**
   * 소비처가 `useInfiniteQuery` 면 `true`.
   *
   * 무한 스크롤 캐시는 `{ pages, pageParams }` 모양이라 `prefetchQuery` 로 담은 평범한 응답을
   * **읽지 못한다.** 모르고 같은 키에 담으면 워밍이 안 되는 정도가 아니라 화면이 깨진다.
   */
  infinite?: boolean;
};

function getPrefetchEntries(view: AppView): PrefetchEntry[] {
  switch (view) {
    case 'attendance': {
      const userId = useAuthStore.getState().user?.id;
      if (!userId) return [];
      return [
        {
          // X3 단계 1d: useMyAttendances 와 같은 namespace 로 통합.
          queryKey: ['attendances', 'me'],
          queryFn: () =>
            import('@/entities/attendance').then((m) => m.attendanceApi.fetchMyAttendances()),
        },
      ];
    }
    case 'bug-reports':
      return [
        {
          // 🔴 `['bug-reports', undefined]` 였다 — 실제 목록은 `['bug-reports','list']`
          // (`useBugReports`)라 hover 로 받아온 응답을 아무도 읽지 못했다. 요청은 나가고,
          // 창을 열면 같은 데이터를 다시 받았다. 팀 설정 `['all-members']` 와 같은 계열이다.
          //
          // 목록은 `useInfiniteQuery` 라 **담는 방식도 함께 바꿔야** 한다(`infinite` 참고).
          // 키만 옮기면 모양이 안 맞는 데이터를 주입해 화면이 깨진다.
          queryKey: ['bug-reports', 'list'],
          infinite: true,
          queryFn: () =>
            import('@/entities/bug-report').then((m) =>
              m.bugReportApi.fetchBugReports({ limit: BUG_REPORT_LIST_PAGE_LIMIT })
            ),
        },
      ];
    case 'team-settings':
      return [
        {
          // 🔴 루트 밖 `['all-members']` 였다 — 실제 조회는 `['teams','all-members']`(useAllMembers)라
          // hover 로 받아온 응답을 아무도 읽지 못했다. 요청은 나가고, 팀 설정을 열면 또 받았다.
          queryKey: ['teams', 'all-members'],
          queryFn: () =>
            import('@/features/team-settings').then((m) => m.teamSettingsApi.fetchAllMembers()),
        },
      ];
    case 'admin-attendance':
      return [
        {
          // X3 단계 1d: entities/attendance namespace + fetchAllAttendances 이전.
          queryKey: ['attendances', 'admin', 'all', undefined, undefined],
          queryFn: () =>
            import('@/entities/attendance').then((m) => m.attendanceApi.fetchAllAttendances()),
        },
      ];
    // prefetch 대상이 없는 뷰 — 기존 default 와 동일하게 빈 배열 반환 (동작 보존).
    // 개발 퀘스트(dev-quests)는 퀘스트 창과 같은 목록 쿼리를 타므로 여기 속한다.
    // 글로벌 정산(campaign-settlement)은 5초 staleTime 짜리 대기열이라 hover 로 미리 받아도
    // 열 때 다시 받는다 — 워밍이 값을 못 한다.
    // 크리에이터 관계(creator-relations)는 창을 열 때 한 번 받는다 — hover 로 미리 당길 만큼
    // 싸지 않고(뷰어별 은닉 판정이 붙는다) 창을 안 열면 버려지는 요청이 된다.
    // 위키(wiki)는 아직 미리 받을 것을 안 정했다. `default` 가 있어도 이름을 적어야 한다 —
    // `switch-exhaustiveness-check` 가 유니온의 모든 가지를 요구한다.
    case 'scenarios':
    case 'quests':
    case 'world-quests':
    case 'dev-quests':
    case 'work-log':
    case 'leaderboard':
    case 'chat':
    case 'hr-management':
    case 'system-admin':
    case 'finance':
    case 'campaign-settlement':
    case 'domestic-settlement':
    case 'today':
    case 'advertiser-console':
    case 'ceo-console':
    case 'usage-dashboard':
    case 'campaign-bonus':
    case 'lounge':
    case 'creator-relations':
    case 'creator-lists':
    case 'wiki':
    default:
      return [];
  }
}

/**
 * 독을 보고 있는 사람. 열 수 없는 창은 워밍하지 않는다 — 권한 없는 사람이 마우스만 올려도
 * 서버 가드에 403 을 맞아 Sentry 에 쌓였다.
 *
 * 쿼리 캐시나 `useAuthStore` 에서 capability 를 읽지 않고 호출자가 넘기는 이유는 위 도크스트링과
 * 같다(capability 조회는 entities 배럴에 있어 정적 import 하면 독 청크로 딸려 온다).
 */
export interface PrefetchViewer {
  level: UserLevel | undefined;
  roles: UserRole[];
  teamRole?: TeamRole;
  teamCapabilities: TeamCapability[];
  /** 서버가 판정한 창 자격 — 자격 없는 창은 미리 불러오지 않는다(IA §1). */
  viewEligibilities: ViewEligibility[];
}

export function prefetchByView(view: AppView, viewer: PrefetchViewer): void {
  if (
    getViewState(view, viewer.teamCapabilities) === 'hidden' ||
    !viewer.level ||
    !canAccessViewFull(
      viewer.level,
      viewer.teamCapabilities,
      view,
      viewer.roles,
      viewer.teamRole,
      viewer.viewEligibilities
    )
  ) {
    return;
  }

  const existing = pendingTimers.get(view);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(() => {
    pendingTimers.delete(view);
    // 1) 데이터 prefetch (TanStack Query) — 일부 뷰만.
    const entries = getPrefetchEntries(view);
    for (const { queryKey, queryFn, infinite } of entries) {
      // 실패해도 토스트·Sentry 를 내지 않는다 — QueryCache.onError(queryClient.ts) 가 이 meta 를 본다.
      // prefetch* 자체는 거부를 삼키지만 쿼리 캐시에는 에러로 남아 전역 핸들러가 발화한다.
      const common = {
        queryKey,
        queryFn,
        staleTime: PREFETCH_STALE_TIME,
        meta: { prefetch: true },
      };
      if (infinite) {
        // 무한 스크롤 캐시는 `{ pages, pageParams }` 모양이라 `prefetchQuery` 로는 채울 수 없다.
        // `initialPageParam` 은 소비 훅과 같아야 첫 페이지가 맞물린다.
        void queryClient.prefetchInfiniteQuery({
          ...common,
          initialPageParam: undefined as string | undefined,
        });
      } else {
        void queryClient.prefetchQuery(common);
      }
    }
    // 2) 페이지 컴포넌트 청크 preload — windowRegistry lazy() 청크 워밍.
    void componentPreloaders[view]?.();
  }, HOVER_DEBOUNCE_MS);
  pendingTimers.set(view, timer);
}
